import React, { useState, useEffect } from 'react';
import { Cookie, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CookieConsentProps {
    onAccept: () => void;
    onReject: () => void;
}

const COOKIE_CONSENT_KEY = 'hr_cookie_consent';

export const useCookieConsent = () => {
    const [consentGiven, setConsentGiven] = useState<boolean | null>(null);
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (stored === null) {
            setShowBanner(true);
            setConsentGiven(null);
        } else {
            setConsentGiven(stored === 'true');
            setShowBanner(false);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
        setConsentGiven(true);
        setShowBanner(false);
    };

    const rejectCookies = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'false');
        setConsentGiven(false);
        setShowBanner(false);
    };

    const resetConsent = () => {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        setConsentGiven(null);
        setShowBanner(true);
    };

    return { consentGiven, showBanner, acceptCookies, rejectCookies, resetConsent };
};

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
    const [showDetails, setShowDetails] = useState(false);
    const { t } = useTranslation('legal');

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
            <div className="max-w-4xl mx-auto">
                <div className="bg-hig-bg-elevated border border-hig-border rounded-xl shadow-2xl overflow-hidden">
                    {/* Main Banner */}
                    <div className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-hig-accent/10 flex items-center justify-center flex-shrink-0">
                                <Cookie size={20} className="text-hig-accent" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-hig-text-primary mb-2">{t('cookie_consent.title')}</h3>
                                <p className="text-sm text-hig-text-secondary mb-4">
                                    {t('cookie_consent.description')}{' '}
                                    <a href="/privacy" className="text-hig-accent hover:underline">{t('cookie_consent.privacy_policy')}</a>
                                </p>

                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={onAccept}
                                        className="btn-primary text-sm py-2 px-4"
                                    >
                                        {t('cookie_consent.accept_all')}
                                    </button>
                                    <button
                                        onClick={onReject}
                                        className="btn-secondary text-sm py-2 px-4"
                                    >
                                        {t('cookie_consent.reject_all')}
                                    </button>
                                    <button
                                        onClick={() => setShowDetails(!showDetails)}
                                        className="text-sm text-hig-text-secondary hover:text-hig-text-primary flex items-center gap-1 transition-colors"
                                    >
                                        <Settings size={14} />
                                        {t('cookie_consent.manage_preferences')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Settings */}
                    {showDetails && (
                        <div className="border-t border-hig-divider p-6 bg-hig-bg-tertiary">
                            <h4 className="font-medium text-hig-text-primary mb-4">{t('cookie_consent.cookie_preferences')}</h4>
                            <div className="space-y-4">
                                {/* Essential */}
                                <div className="flex items-center justify-between p-3 bg-hig-bg-secondary rounded-lg">
                                    <div>
                                        <p className="font-medium text-hig-text-primary text-sm">{t('cookie_consent.essential_cookies')}</p>
                                        <p className="text-xs text-hig-text-tertiary">{t('cookie_consent.essential_description')}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-hig-text-tertiary">{t('cookie_consent.always_active')}</span>
                                        <div className="w-10 h-5 bg-hig-accent rounded-full flex items-center justify-end px-0.5">
                                            <div className="w-4 h-4 bg-white rounded-full"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Analytics */}
                                <div className="flex items-center justify-between p-3 bg-hig-bg-secondary rounded-lg">
                                    <div>
                                        <p className="font-medium text-hig-text-primary text-sm">{t('cookie_consent.analytics_cookies')}</p>
                                        <p className="text-xs text-hig-text-tertiary">{t('cookie_consent.analytics_description')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" defaultChecked />
                                        <div className="w-10 h-5 bg-hig-bg-tertiary peer-checked:bg-hig-accent rounded-full transition-colors flex items-center peer-checked:justify-end justify-start px-0.5">
                                            <div className="w-4 h-4 bg-white rounded-full"></div>
                                        </div>
                                    </label>
                                </div>

                                {/* Marketing */}
                                <div className="flex items-center justify-between p-3 bg-hig-bg-secondary rounded-lg">
                                    <div>
                                        <p className="font-medium text-hig-text-primary text-sm">{t('cookie_consent.marketing_cookies')}</p>
                                        <p className="text-xs text-hig-text-tertiary">{t('cookie_consent.marketing_description')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" />
                                        <div className="w-10 h-5 bg-hig-bg-tertiary peer-checked:bg-hig-accent rounded-full transition-colors flex items-center peer-checked:justify-end justify-start px-0.5">
                                            <div className="w-4 h-4 bg-white rounded-full"></div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={onAccept}
                                    className="btn-primary text-sm py-2 px-4"
                                >
                                    {t('cookie_consent.save_preferences')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Wrapper component that handles visibility
export const CookieConsentBanner: React.FC = () => {
    const { showBanner, acceptCookies, rejectCookies } = useCookieConsent();

    if (!showBanner) return null;

    return (
        <CookieConsent
            onAccept={acceptCookies}
            onReject={rejectCookies}
        />
    );
};

export default CookieConsent;
